import { useState, useEffect, useRef } from 'react';
import { User, Crown, LogOut, ChevronDown, Users, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfileData } from '../services/api';
import { getFullUrl } from '../config/api';

interface ProfileProps {
  onSignOut?: () => void;
  onMyProfileClick?: () => void;
  onSettingsClick?: () => void;
  onUserManagementClick?: () => void;
  onProfileClick?: () => void; // New prop to handle profile picture click
}

export default function Profile({ onSignOut, onMyProfileClick, onSettingsClick, onUserManagementClick, onProfileClick }: ProfileProps) {
  const { user, logout } = useAuth();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Function to refresh profile data
  const refreshProfile = async () => {
    if (user) {
      try {
        setLoadingProfile(true);
        const response = await getUserProfileData();
        if (response.success && response.data) {
          const profileData = response.data.data || response.data;
          if (profileData.profile?.profile_picture) {
            const fullUrl = getFullUrl(profileData.profile.profile_picture);
            setProfilePicture(fullUrl);
          } else {
            setProfilePicture(null);
          }
          
          // Dispatch event to refresh user data in AuthContext
          window.dispatchEvent(new CustomEvent('refreshUserData'));
        }
      } catch (error) {
        console.error('Error refreshing profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    }
  };

  // Fetch profile picture when component mounts
  useEffect(() => {
    refreshProfile();
  }, [user]);

  // Listen for profile updates and refresh requests
  useEffect(() => {
    const handleProfilePictureUpdate = () => {
      refreshProfile();
    };

    const handleProfileRefresh = () => {
      refreshProfile();
    };

    // Listen for custom events
    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);
    window.addEventListener('refreshProfile', handleProfileRefresh);
    
    return () => {
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
      window.removeEventListener('refreshProfile', handleProfileRefresh);
    };
  }, [user]);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };
  
  const handleProfilePictureClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onProfileClick) {
      onProfileClick();
      setIsProfileDropdownOpen(false);
    } else if (onMyProfileClick) {
      onMyProfileClick();
      setIsProfileDropdownOpen(false);
    }
  };

  const handleMouseLeave = () => {
    // Set a timeout to close the dropdown, but clear it if mouse enters dropdown
    timeoutRef.current = setTimeout(() => {
      setIsProfileDropdownOpen(false);
    }, 150); // Small delay to allow mouse to move to dropdown
  };

  const handleMouseEnter = () => {
    // Clear timeout when mouse enters profile area
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleSignOut = async () => {

    setIsProfileDropdownOpen(false);
    try {
      await logout();
      if (onSignOut) {
        onSignOut();
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="flex items-center">
      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .profile-dropdown {
          animation: fadeInDown 0.2s ease-out;
        }
      `}</style>
      <div className="relative" onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter}>
        <button
          onClick={handleButtonClick}
          className="group flex items-center gap-3 bg-white hover:bg-gray-50 px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 border border-gray-200 hover:border-gray-300 hover:shadow-sm"
          aria-label="Profile menu"
          aria-expanded={isProfileDropdownOpen}
        >
          <div 
            className="w-11 h-11 bg-gradient-to-br from-orange-500 to-purple-600 rounded-full flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-200 overflow-hidden profile-picture-area cursor-pointer ring-2 ring-white"
            onClick={handleProfilePictureClick}
          >
            {loadingProfile ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : profilePicture ? (
              <img 
                src={profilePicture} 
                alt="Profile" 
                className="w-full h-full object-cover"
                onError={() => setProfilePicture(null)}
              />
            ) : (
              <User className="w-6 h-6 text-white" />
            )}
          </div>
          <div className="hidden lg:flex items-center gap-2">
            <div className="flex flex-col items-start">
              <span className="text-base font-semibold text-gray-800 group-hover:text-gray-900 transition-colors">
                {user ? `${user.firstName} ${user.lastName}` : 'User'}
              </span>
              {/* Plan Badge - Hidden on top, shown only in dropdown */}
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-500 transition-all duration-200 ${isProfileDropdownOpen ? 'rotate-180 text-gray-700' : 'group-hover:text-gray-700'}`} />
        </button>

        {/* Profile Dropdown Menu */}
        {isProfileDropdownOpen && (
          <div 
            className="profile-dropdown absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200/60 backdrop-blur-sm overflow-hidden z-[9999]" 
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Profile Header */}
            <div className="p-6 bg-gradient-to-r from-orange-50 to-purple-50 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-purple-600 rounded-full flex items-center justify-center overflow-hidden ring-2 ring-white shadow-md">
                  {loadingProfile ? (
                    <div className="w-7 h-7 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : profilePicture ? (
                    <img 
                      src={profilePicture} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                      onError={() => setProfilePicture(null)}
                    />
                  ) : (
                    <User className="w-7 h-7 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {user ? `${user.firstName} ${user.lastName}` : 'User'}
                  </h3>
                  <p className="text-base text-gray-600 truncate">{user?.email || 'user@email.com'}</p>
                  {user?.is_premium && (
                    <div className="flex items-center space-x-1.5 mt-2">
                      <Crown className="w-5 h-5 text-yellow-500" fill="currentColor" />
                      <span className="text-sm text-yellow-700 font-semibold">Premium Member</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Options */}
            <div className="py-2">
              <button 
                onClick={() => {
                  if (onMyProfileClick) {
                    onMyProfileClick();
                  }
                  setIsProfileDropdownOpen(false);
                }}
                className="w-full flex items-center gap-4 px-5 py-5 text-left text-gray-700 hover:bg-gray-50 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <User className="w-7 h-7 text-blue-600" />
                </div>
                <span className="text-lg font-semibold">My Profile</span>
              </button>

              {/* Settings - Only for superusers */}
              {user?.is_superuser && (
                <button 
                  onClick={() => {
                    if (onSettingsClick) {
                      onSettingsClick();
                    }
                    setIsProfileDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-4 px-5 py-5 text-left text-gray-700 hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center group-hover:bg-gray-100 transition-colors">
                    <Settings className="w-7 h-7 text-gray-600" />
                  </div>
                  <span className="text-lg font-semibold">Settings</span>
                </button>
              )}

              {/* User Management - Only for superusers, admins, or staff */}
              {(user?.is_superuser || user?.is_admin || user?.is_staff) && (
                <button 
                  onClick={() => {
                    if (onUserManagementClick) {
                      onUserManagementClick();
                    }
                    setIsProfileDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-4 px-5 py-5 text-left text-gray-700 hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 transition-colors">
                    <Users className="w-7 h-7 text-teal-600" />
                  </div>
                  <span className="text-lg font-semibold">User Management</span>
                </button>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100 my-1"></div>

            {/* Logout */}
            <div className="py-2">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-4 px-5 py-5 text-left text-red-600 hover:bg-red-50 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <LogOut className="w-7 h-7 text-red-600" />
                </div>
                <span className="text-lg font-semibold">Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
